<template>
  <transition name="fade">
    <div
      v-if="!loading"
      class="col-auto"
    >
      <div v-if="!user.isLoggedIn" class="d-flex align-items-center">
        <template v-if="isMobile">
          <router-link :to="{ name: 'LoginPage' }" class="button-underline-fix">
            <button class="mobile-login-button font-weight-bold">LOGIN</button>
          </router-link>
        </template>
        <template v-else>
          <router-link :to="{ name: 'SignUpPage' }" class="button-underline-fix">
            <button class="sign-up-button mr-3 font-weight-bold">SIGNUP</button>
          </router-link>
          <router-link :to="{ name: 'LoginPage' }">
            <button class="login-button mr-3 font-weight-bold">LOGIN</button>
          </router-link>
        </template>
      </div>

      <div
        v-if="user.isLoggedIn"
        class="d-flex align-items-center align-content-center content-account-info"
      >
      
        <div v-if="!isMobile && canUseCashier" class="pr-4">
          <button class="deposit" @click="showDepositModal()">WALLET</button>
        </div>
        <div class="account-info order-1 order-md-0">
          <!-- Balance in large text position (tablet and desktop) -->
          <div
            class="account-balance d-none d-md-block"
            @click="goToPendingBets"
          >
            Balance
          </div>
          <div
            class="account-name d-none d-md-block"
            @click="goToPendingBets"
          >
            {{ balanceSign }}${{
              formatBalance(Math.abs(user.displayBalance), { forMobile: false })
            }}
          </div>
        </div>

        <!-- Icon wrapper to keep icons horizontal -->
        <div v-if="!isMobile" class="icon-wrapper d-flex align-items-center">
          <div
            ref="accountLogo"
            tabindex="0"
            class="account-logo d-flex justify-content-center ml-lg-3"
            @click="toggleAccountPopUp('icon')"
            @keyup.enter="toggleAccountPopUp('icon')"
          >
            <!-- <font-awesome-icon :icon="faUser" size="2x" /> -->
            <img src="/icons/userwarrow.svg" />
          </div>

          <!-- Dropdowns positioned relative to icon-wrapper -->
          <account-dropdown
            :show-popup="showAccountPopUp"
            @close-pop-up="toggleAccountPopUp('component')"
          />
        </div>
        <notification-inbox
          :show="showNotificationInbox"
          :from-alert="inboxOpenedFromAlert"
          :anchor-rect="notificationAnchorRect"
          @close="handleInboxClose"
        />
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useUserStore } from '@/stores/user';
import { useNotificationStore } from '@/stores/notification';
import { useWindowSize } from '@/composables/useWindowSize';
import { useRouter, useRoute } from 'vue-router';
import { onMounted, onUnmounted } from 'vue';
import { emitter } from '@/core/emitter.js';
import { userCanUseCashier } from '@/consts/constants';
import { balanceDisplaySign } from '@/core/utils';
import { PostHogUtils } from '@/core/PostHogUtils';

// Components
import AccountDropdown from '@/components/global/header/AccountDropdown.vue';
import NotificationInbox from '@/components/global/header/NotificationInbox.vue';

//icons
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { storeToRefs } from 'pinia';

import { provide } from 'vue';

// init stores
const userStore = useUserStore();
const { user } = storeToRefs(userStore);
// Wallet access — single source of truth (blocked affiliate codes / usernames /
// demo can't use the cashier, so they get no wallet entry point at all).
const canUseCashier = computed(() => userCanUseCashier(userStore.user));
const notificationStore = useNotificationStore();
const router = useRouter();
const route = useRoute();
const { width } = useWindowSize();
const loading = ref(false);
const showAccountPopUp = ref(false);
const showNotificationInbox = ref(false);
const inboxOpenedFromAlert = ref(false);
const accountLogo = ref(null);
const balanceElement = ref(null);
const notificationAnchorRect = ref(null);

// Create a computed property that flattens the ignore elements
const ignoreElements = computed(() =>
  [accountLogo.value, balanceElement.value].filter(Boolean),
);

provide('ignoreElement', ignoreElements);

const isMobile = computed(() => {
  return width.value < 768;
});

// The magnitude is rendered separately (abs + 2 decimals), so the sign has to
// agree with what actually prints — a balance of -0.000321 shows as $0.00 and
// must not be signed "-$0.00".
const balanceSign = computed(() =>
  balanceDisplaySign(user.value.displayBalance),
);

function formatBalance(balance, { forMobile } = {}) {
  const useMobileFormat =
    typeof forMobile === 'boolean' ? forMobile : isMobile.value;

  if (useMobileFormat) {
    const options =
      balance >= 1000
        ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return balance.toLocaleString('en-US', options);
  }

  return balance.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

async function showDepositModal() {
  if (userStore.user?.veriffData?.['required'] === true && userStore.user?.veriffData?.['status'] !== 'approved') {
    PostHogUtils.markKycRequired();
    emitter.emit('showVeriffModal');
  } else {
    emitter.emit('showDepositModal');
  }
}

function goToPendingBets() {
  if (route.name !== 'MyBetsPage' || route.params.type !== 'active-wagers') {
    router.push('/my-bets/active-wagers');
  }
}

function toggleAccountPopUp(event) {
  if (event === 'icon') {
    showAccountPopUp.value = !showAccountPopUp.value;
    // Close notification inbox if it's open
    if (showAccountPopUp.value) {
      showNotificationInbox.value = false;
    }
  } else {
    showAccountPopUp.value = false;
  }
}

function toggleNotificationInbox(fromAlert = false) {
  showNotificationInbox.value = !showNotificationInbox.value;
  inboxOpenedFromAlert.value = fromAlert;
  if (fromAlert) {
    notificationAnchorRect.value = null;
  }
  // Close account popup if it's open
  if (showNotificationInbox.value) {
    showAccountPopUp.value = false;
    // Mark all unread notifications as read when opening the inbox
    notificationStore.markAllInboxNotificationsAsRead();
  } else {
    inboxOpenedFromAlert.value = false;
  }
}

function handleInboxClose() {
  showNotificationInbox.value = false;
  inboxOpenedFromAlert.value = false;
}

// Listen for events to open notification inbox
function handleOpenNotificationInbox(data) {
  const shouldToggle = data?.toggle ?? false;
  const fromAlert = data?.fromAlert || false;
  if (data?.rect) {
    notificationAnchorRect.value = data.rect;
  }

  if (shouldToggle) {
    toggleNotificationInbox(fromAlert);
    return;
  }

  if (!showNotificationInbox.value) {
    toggleNotificationInbox(fromAlert);
  }
}

onMounted(() => {
  emitter.on('openNotificationInbox', handleOpenNotificationInbox);
});

onUnmounted(() => {
  emitter.off('openNotificationInbox', handleOpenNotificationInbox);
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/theme/variables.scss' as theme;

.log-in-link {
  color: theme.$grey-702;
  font-size: theme.$font-size-sm;
  transition: color 0.2s ease;
  border-left: 1px solid theme.$grey-701;

  &:hover {
    color: theme.$white;
  }
}

.sign-up-button {
  background: theme.$white;
  transition: color ease 0.2s;
  color: theme.$color-primary;
  text-decoration: none;

  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    font-size: 12px;
    padding: 6px;
  }

  &:hover {
    text-decoration: none;

    transform: scale(1.025);
    transition: transform 0.3s;
  }
}

.mobile-login-button {
  background: theme.$white;
  color: theme.$color-primary;
  text-decoration: none;
  font-size: 14px;
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  white-space: nowrap;
  transition: transform 0.3s;

  &:hover {
    transform: scale(1.025);
  }
}

.login-button {
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    font-size: 12px;
    padding: 6px;
  }
}

.button-underline-fix {
  text-decoration: none;
}

button {
  flex: 1 0 auto;
  font-weight: theme.$font-weight-light;
  padding: 0.5rem 2rem;
  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    font-size: 1rem;
    padding: 0.5rem 1rem;
  }
}

.Bonus {
  background-color: theme.$blue-100;
  font-weight: bold;

  &:disabled {
    background-color: theme.$grey-1800;
    color: theme.$white;
    cursor: pointer;
  }
}

.account-name {
  color: theme.$grey-300_01;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: theme.$white;
  }
}

.account-info {
  color: theme.$white;
  font-weight: theme.$font-weight-normal;
  text-align: right;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and (max-width: map.get(theme.$grid-breakpoints, lg)) {
    align-items: center;
    text-align: center;
    width: 100%;
  }
}

.account-balance {
  font-size: theme.$font-size-xs;
  color: theme.$grey-703;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: theme.$white;
  }

  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    font-size: 0.6rem;
    padding-top: 5px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  @media screen and (max-width: 370px) {
    font-size: 0.5rem;
  }
}

.account-balance--mobile {
  color: theme.$white;
  font-weight: theme.$font-weight-semibold;
  text-align: center;
  width: 100%;
  margin-top: 0.25rem;

  span {
    display: inline-block;
  }
}

// Separator line between icons - equal padding
.icon-separator {
  width: 1px;
  height: 20px;
  background-color: theme.$grey-703;
  opacity: 0.5;
  margin: 0 12px;
}

.notification-bell {
  cursor: pointer;
  position: relative;
  font-size: 1.1rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    font-size: 1rem;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    font-size: 0.9rem;
  }
  @media screen and (max-width: 370px) {
    font-size: 0.8rem;
  }

  .bell-icon {
    transition: all 0.2s ease;
  }

  .notification-dot {
    position: absolute;
    top: -2px;
    right: -1px;
    width: 5px;
    height: 5px;
    background-color: #d28f01;
    border-radius: 50%;
    pointer-events: none;
  }

  // Default state - no notifications (dimmed bell)
  &:not(.has-notifications) {
    .bell-icon {
      color: theme.$grey-703;
    }

    &:hover {
      .bell-icon {
        color: theme.$grey-300;
        transform: scale(1.05);
      }
    }

    &:active {
      .bell-icon {
        transform: scale(0.95);
      }
    }
  }

  // Has notifications state - bright bell with blue dot
  &.has-notifications {
    .bell-icon {
      color: theme.$white;
    }

    &:hover {
      .bell-icon {
        color: theme.$grey-300;
        transform: scale(1.05);
      }
    }

    &:active {
      .bell-icon {
        transform: scale(0.95);
      }
    }
  }
}

.account-logo {
  color: theme.$white;
  font-size: 0.85rem;
  cursor: pointer;
  // The div carries tabindex="0" for keyboard access, so clicking it also
  // focuses it and the browser paints its default focus ring. Drop the ring
  // for pointer focus only — Tab focus keeps it.
  &:focus:not(:focus-visible) {
    outline: none;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    font-size: 0.8rem;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    font-size: 0.75rem;
  }
  @media screen and (max-width: 370px) {
    font-size: 0.65rem;
  }
}

.popup {
  position: absolute;
  right: 50px;
  top: 0px;
  width: 300px;
  background: theme.$white_02;
  box-shadow: 0 2px 10px theme.$black-101;
  padding: 1rem 0;
  border-radius: 10px;
  color: theme.$grey-2300;
  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    right: 25px;
    top: 20px;
    width: 250px;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    right: 5px;
  }

  &-header {
    height: 75px;
    font-weight: theme.$font-weight-normal;
    cursor: pointer;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    color: theme.$blue-104;
    border-bottom: 1px solid theme.$blue-200;

    .wrapper {
      width: 100%;
      margin: 0;
    }

    &-menu {
      padding-block: 0.8rem;
      a,
      div {
        text-decoration: none;
        font-size: theme.$font-size-sm;
        font-weight: theme.$font-weight-normal;
        display: flex;
        align-items: center;
        width: 100%;
        padding: 0.6rem 1.5rem;
        color: theme.$grey-2300;
      }

      &:hover {
        cursor: pointer;
        background: theme.$color-primary;
        color: theme.$white; //#343B86
        font-weight: theme.$font-weight-semibold;
      }

      &.logout-link {
        cursor: pointer;
        span {
          &.text-blue {
            color: theme.$color-primary;
          }
        }
        &:hover {
          font-weight: theme.$font-weight-semibold;
          background: theme.$color-primary;
          color: theme.$white;

          img {
            filter: brightness(0) invert(1);
            .icon {
              color: theme.$white;
            }
            span {
              color: theme.$white;
            }
          }
        }
      }
    }
  }
}

.icon {
  width: 17px;
  height: 17px;
  &-blue {
    color: theme.$color-primary;
  }
}
.column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  h3 {
    font-size: 1.125rem;
  }
}
.with-right-border {
  border-right: 1px solid theme.$blue-200;
}
.font-white {
  color: theme.$white;
}
.num {
  font-weight: bold;

  &:hover {
    color: theme.$grey-300;
  }
  // Location and Size of alerts/notifications relative to screen. If you change this you should also change notification.vue size.
}
.notifications-wrapper {
  position: fixed;
  right: 2%;
  top: 5%;
  z-index: 9999;
  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    right: 20%;
    top: 20px;
    width: 250px;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    right: 6%;
  }
}
.margin-zero {
  margin: 0px 0px 0px 25px !important;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.content-account-info {
  align-items: center;

  @media screen and (max-width: map.get(theme.$grid-breakpoints, lg)) {
    // flex-direction: column;
    text-align: center;
  }
}

.icon-wrapper {
  position: relative;
  // Keep icons horizontal at all screen sizes
  flex-direction: row !important;
  justify-content: flex-end;
  width: 100%;

  @media screen and (max-width: map.get(theme.$grid-breakpoints, lg)) {
    justify-content: center;
    width: 100%;
    margin-top: 0.25rem;
  }
}
.inviteOption {
  padding: unset !important;
}
.divBoxNew {
  justify-content: end;
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    padding-left: 15px !important;
    padding-right: 15px !important;
  }
}
.boxNew {
  padding: 0;
  background-color: theme.$blue-502;
  font-size: 12px;
  font-weight: bold;
  padding: 0.1rem 0.2rem;
  border-radius: 3px;
}
.deposit {
  background-color: theme.$white;
  font-weight: bold;
  color: theme.$color-primary;
  padding: 0.33rem 0.75rem;
  border-radius: 4px;
  min-height: 40px;

  @media screen and (max-width: map.get(theme.$grid-breakpoints, lg)) and (min-width: map.get(theme.$grid-breakpoints, md)) {
    padding: 0.25rem 0.5rem;
    min-height: 32px;
    font-size: 12px;
  }
}
</style>
